import {
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Add16Regular, ArrowSwap16Regular, BookOpen16Regular, Gavel16Regular, Lightbulb16Regular, ShieldError16Regular, Warning16Regular } from "@fluentui/react-icons";
import { useMemo, useState, type ReactElement } from "react";
import { CellLabel, type CellTone } from "@/components/data-table";
import type { ProjectRecord, RecordKind, RecordSeverity, RecordStatus } from "@/engine/types";
import { RECORD_KIND_LABEL, RECORD_STATUSES, recordCode, recordPhase, recordStatusLabel, SEVERITY_LABEL, SEVERITY_ORDER } from "@/store/record-ops";
import { recordStatusGlyph, severityGlyph, type GlyphSize } from "./record-glyphs";

const useStyles = makeStyles({
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    maxWidth: "100%",
    minWidth: 0,
    height: "24px",
    padding: `0 ${tokens.spacingHorizontalS}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    textAlign: "left",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  chipClosed: {
    color: tokens.colorNeutralForeground3,
  },
  code: {
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
  },
  title: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  kindIcon: {
    display: "inline-flex",
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  control: {
    minHeight: "28px",
  },
  tag: {
    maxWidth: "260px",
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  optionSub: {
    marginLeft: "auto",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    flexShrink: 0,
  },
  list: {
    maxHeight: "320px",
    overflowY: "auto",
  },
  valueGlyph: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
    maxWidth: "100%",
  },
  glyph: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  },
});

const KIND_ICON: Record<RecordKind, ReactElement> = {
  issue: <Warning16Regular />,
  risk: <ShieldError16Regular />,
  decision: <Gavel16Regular />,
  policy: <BookOpen16Regular />,
  change: <ArrowSwap16Regular />,
  assumption: <Lightbulb16Regular />,
};

export function RecordKindIcon({ kind, className }: { kind: RecordKind; className?: string }) {
  const styles = useStyles();
  return (
    <span className={mergeClasses(styles.kindIcon, className)} aria-label={RECORD_KIND_LABEL[kind]} role="img">
      {KIND_ICON[kind]}
    </span>
  );
}

export function recordKindIcon(kind: RecordKind): ReactElement {
  return KIND_ICON[kind];
}

function statusTone(status: RecordStatus): CellTone {
  return recordPhase(status) === "closed" ? "muted" : "default";
}

export function RecordStatusBadge({ kind, status, size = 12 }: { kind: RecordKind; status: RecordStatus; size?: GlyphSize }) {
  const styles = useStyles();
  return (
    <span className={styles.valueGlyph}>
      <span className={styles.glyph} aria-hidden>
        {recordStatusGlyph(status, size, true)}
      </span>
      <CellLabel tone={statusTone(status)}>{recordStatusLabel(kind, status)}</CellLabel>
    </span>
  );
}

function severityTone(severity: RecordSeverity): CellTone {
  return severity === "low" ? "muted" : "default";
}

export function SeverityBadge({ severity, size = 12 }: { severity: RecordSeverity | undefined; size?: GlyphSize }) {
  const styles = useStyles();
  if (!severity) return <CellLabel tone="muted">—</CellLabel>;
  return (
    <span className={styles.valueGlyph}>
      <span className={styles.glyph} aria-hidden>
        {severityGlyph(severity, size)}
      </span>
      <CellLabel tone={severityTone(severity)}>{SEVERITY_LABEL[severity]}</CellLabel>
    </span>
  );
}

/** 서랍 셀렉트와 팝오버가 같이 쓰는 16px 표식. */
export function recordStatusOptions(kind: RecordKind) {
  return RECORD_STATUSES[kind].map((status) => ({
    value: status,
    label: recordStatusLabel(kind, status),
    icon: recordStatusGlyph(status, 16, true),
  }));
}

export function severityOptions() {
  return [
    { value: "", label: "미정", icon: severityGlyph(undefined) },
    ...SEVERITY_ORDER.map((severity) => ({
      value: severity,
      label: SEVERITY_LABEL[severity],
      icon: severityGlyph(severity),
    })),
  ];
}

/** Code + title button; used wherever a record is referenced from elsewhere. */
export function RecordChip({ record, onOpen }: { record: ProjectRecord; onOpen: (id: string) => void }) {
  const styles = useStyles();
  const code = recordCode(record);
  const label = `${RECORD_KIND_LABEL[record.kind]} ${code} ${record.title} · ${recordStatusLabel(record.kind, record.status)}`;
  return (
    <button type="button" className={mergeClasses(styles.chip, recordPhase(record.status) === "closed" && styles.chipClosed)} title={label} aria-label={`${label} 열기`} onClick={() => onOpen(record.id)}>
      <span className={styles.kindIcon} aria-hidden>
        {KIND_ICON[record.kind]}
      </span>
      <span className={styles.code}>{code}</span>
      <span className={styles.title}>{record.title || "제목 없음"}</span>
    </button>
  );
}

export interface RefOption {
  id: string;
  label: string;
  code?: string;
  sub?: string;
  media?: ReactElement;
}

/** Multi-select over a fixed option list (execution tasks, other records). */
export function RefPicker({
  value,
  onChange,
  options,
  placeholder,
  ariaLabel,
  emptyText = "일치하는 항목이 없습니다",
}: {
  value: string[];
  onChange: (next: string[]) => void;
  options: RefOption[];
  placeholder: string;
  ariaLabel: string;
  emptyText?: string;
}) {
  const styles = useStyles();
  const [query, setQuery] = useState("");
  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const q = query.trim().toLowerCase();
  const matching = useMemo(
    () => options.filter((o) => !value.includes(o.id) && (!q || `${o.code ?? ""} ${o.label}`.toLowerCase().includes(q))),
    [options, value, q],
  );
  return (
    <TagPicker
      selectedOptions={value}
      onOptionSelect={(_, data) => {
        if (data.value === "__none") return;
        onChange(data.selectedOptions.filter((x) => x !== "__none" && byId.has(x)));
        setQuery("");
      }}
      positioning={{ position: "below", align: "start", fallbackPositions: ["above"], matchTargetSize: "width" }}
      size="medium"
    >
      <TagPickerControl className={styles.control}>
        <TagPickerGroup aria-label={ariaLabel}>
          {value.map((id) => {
            const o = byId.get(id);
            if (!o) return null;
            const text = `${o.code ? `${o.code} ` : ""}${o.label}`;
            return (
              <InteractionTag key={id} value={id} shape="rounded" size="small" className={styles.tag} title={text}>
                <InteractionTagPrimary hasSecondaryAction media={o.media}>
                  {text}
                </InteractionTagPrimary>
                <InteractionTagSecondary aria-label={`${text} 제거`} />
              </InteractionTag>
            );
          })}
        </TagPickerGroup>
        <TagPickerInput aria-label={ariaLabel} placeholder={value.length ? "" : placeholder} value={query} onChange={(e) => setQuery(e.target.value)} />
      </TagPickerControl>
      <TagPickerList className={styles.list}>
        {matching.length === 0 ? (
          <TagPickerOption value="__none" aria-disabled="true">
            {emptyText}
          </TagPickerOption>
        ) : (
          matching.map((o) => (
            <TagPickerOption key={o.id} value={o.id} media={o.media} text={`${o.code ?? ""} ${o.label}`.trim()}>
              <span className={styles.optionRow}>
                {o.code && <span className={styles.code}>{o.code}</span>}
                <span className={styles.title}>{o.label}</span>
                {o.sub && <span className={styles.optionSub}>{o.sub}</span>}
              </span>
            </TagPickerOption>
          ))
        )}
      </TagPickerList>
    </TagPicker>
  );
}

const CREATE_VALUE = "__create";
const NONE_VALUE = "__none";

/**
 * One input for "link an existing item or create a new one" (Linear·Jira sub-issue style).
 * Typing lists `+ 「text」 새로 만들기` first (Enter creates it), then matching unlinked options (↓ to pick and link).
 * Linked items are shown by the caller as a list, not as tags here.
 */
export function RefSearchAdd({
  exclude,
  options,
  onPick,
  onCreate,
  placeholder,
  ariaLabel,
  createLabel = (text) => `「${text}」 새로 만들기`,
  autoFocus,
  onDismiss,
}: {
  /** ids already linked (hidden from the list) */
  exclude: string[];
  options: RefOption[];
  onPick: (id: string) => void;
  /** omit to allow linking only */
  onCreate?: (text: string) => void;
  placeholder: string;
  ariaLabel: string;
  createLabel?: (text: string) => string;
  autoFocus?: boolean;
  /** Esc or leaving the empty field (inline "add row" returns to its resting state) */
  onDismiss?: () => void;
}) {
  const styles = useStyles();
  const [query, setQuery] = useState("");
  const text = query.trim();
  const q = text.toLowerCase();
  const matching = useMemo(() => {
    const skip = new Set(exclude);
    return options.filter((o) => !skip.has(o.id) && (!q || `${o.code ?? ""} ${o.label}`.toLowerCase().includes(q))).slice(0, 50);
  }, [options, exclude, q]);
  return (
    <TagPicker
      selectedOptions={[]}
      onOptionSelect={(_, data) => {
        const v = data.value;
        if (v === NONE_VALUE) return;
        if (v === CREATE_VALUE) {
          if (text && onCreate) onCreate(text);
        } else if (v) onPick(v);
        setQuery("");
      }}
      positioning={{ position: "below", align: "start", fallbackPositions: ["above"], matchTargetSize: "width" }}
      size="medium"
      appearance="underline"
    >
      <TagPickerControl className={styles.control} expandIcon={null}>
        <TagPickerInput
          aria-label={ariaLabel}
          placeholder={placeholder}
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            if (e.key === "Escape") {
              // clear first, then leave; never let Esc fall through and close the surrounding sheet
              e.preventDefault();
              if (query) setQuery("");
              else onDismiss?.();
              return;
            }
            // Enter with nothing highlighted creates from the typed text (↓ first to link an existing one)
            if (e.key === "Enter" && text && onCreate && !e.currentTarget.getAttribute("aria-activedescendant")) {
              e.preventDefault();
              onCreate(text);
              setQuery("");
            }
          }}
          onBlur={(e) => {
            // moving focus to another control ends an empty edit; picking an option (not focusable) does not
            if (query.trim()) return;
            const next = e.relatedTarget as HTMLElement | null;
            if (next && !next.closest('[role="listbox"]')) onDismiss?.();
          }}
        />
      </TagPickerControl>
      <TagPickerList className={styles.list}>
        {text && onCreate && (
          <TagPickerOption value={CREATE_VALUE} media={<Add16Regular />} text={text}>
            {createLabel(text)}
          </TagPickerOption>
        )}
        {matching.length === 0 && !(text && onCreate) ? (
          <TagPickerOption value={NONE_VALUE} aria-disabled="true">
            {text ? "일치하는 항목이 없습니다" : "연결할 항목이 없습니다"}
          </TagPickerOption>
        ) : (
          matching.map((o) => (
            <TagPickerOption key={o.id} value={o.id} media={o.media} text={`${o.code ?? ""} ${o.label}`.trim()}>
              <span className={styles.optionRow}>
                {o.code && <span className={styles.code}>{o.code}</span>}
                <span className={styles.title}>{o.label}</span>
                {o.sub && <span className={styles.optionSub}>{o.sub}</span>}
              </span>
            </TagPickerOption>
          ))
        )}
      </TagPickerList>
    </TagPicker>
  );
}
